import React from "react";
import { Helmet } from "react-helmet-async";

const TermsAndConditions = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions | Luxuriya Homes - Luxury Interior Design</title>
        <meta 
          name="description" 
          content="Read the Terms and Conditions of Luxuriya Homes. We ensure transparency, trust, and professional excellence in our luxury interior design services." 
        />
        <meta 
          name="keywords" 
          content="Terms and Conditions, Luxuriya Homes Policy, Interior Design Contracts, Luxury Interiors India" 
        />
        <link rel="canonical" href="https://www.luxuriyahomes.com/terms-and-conditions" />
      </Helmet>

      <div className="bg-neutral-50 min-h-screen text-neutral-800 font-sans">
        
        {/* HERO / HEADER */}
        <header
          className="relative h-[50vh] md:h-[65vh] flex items-center justify-center text-white"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/70"></div>

          {/* Content */}
          <div className="relative z-10 text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif tracking-widest text-[#D4AF37] mb-4">
              Terms & Conditions
            </h1>

            <div className="w-24 md:w-32 h-[2px] bg-[#D4AF37] mx-auto mt-6 md:mt-8 mb-8 md:mb-10"></div>

            <p className="max-w-xl md:max-w-2xl mx-auto text-neutral-300 text-base md:text-lg leading-relaxed px-2">
              Designed to ensure transparency, trust, and professional excellence in every project we undertake.
            </p>
          </div>
        </header>

        {/* CONTENT */}
        <main className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
          
          {/* INTRO */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
            <p className="text-base md:text-lg leading-loose text-neutral-600">
              Welcome to{" "}
              <span className="font-semibold text-black">
                Luxuriya Homes
              </span>.  
              Our goal is to provide you with a refined, transparent, and world-class luxury
              interior experience. The terms outlined below ensure our
              professional standards and mutual trust.
            </p>
          </div>

          {/* SECTIONS */}
          <div className="space-y-8 md:space-y-12">
            <Section title="1. Services & Scope">
              Luxuriya Homes provides end-to-end interior design solutions. 
              All designs and 3D renders remain our intellectual property until the full payment 
              for the project is cleared. Execution will strictly follow the final approved drawings.
            </Section>

            <Section title="2. Payment Policy">
              <ul className="list-disc pl-5 md:pl-6 space-y-2 md:space-y-3">
                <li>
                  <strong>Booking Amount:</strong> A 10% advance is mandatory for project confirmation.
                </li>
                <li>
                  <strong>Design Phase:</strong> 40% payment is due before design finalization.
                </li>
                <li>
                  <strong>Execution Phase:</strong> The remaining payment will be collected according to agreed milestones.
                </li>
                <li>
                  Delayed payments may impact the project timeline.
                </li>
              </ul>
            </Section>

            <Section title="3. Design Revisions">
              We offer{" "}
              <strong>3 Major Design Revisions</strong> complimentary with every project.
              Subsequent changes will be processed with an additional cost.
            </Section>

            <Section title="4. Timelines & Delays">
              We adhere to committed timelines; however, Luxuriya Homes is not responsible for 
              delays caused by external factors such as material availability, permissions, or site conditions.
            </Section>

            <Section title="5. Warranty & Maintenance">
              A{" "}
              <strong>5-Year Manufacturing Warranty</strong> is applicable on custom furniture and woodwork.
              Warranties for electrical fittings, fabrics, and appliances depend on the respective manufacturers' terms.
            </Section>
          </div>

          {/* FOOTER NOTE */}
          <div className="mt-16 md:mt-24 pt-10 border-t border-neutral-200 text-center text-sm text-neutral-500">
            <p>Last Updated: January 2026</p>
            <p className="mt-3">
              For queries, please contact:{" "}
              <a
                href="mailto:support@luxuriyahomes.com"
                className="text-[#D4AF37] hover:underline"
              >
                support@luxuriyahomes.com
              </a>
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

/* SECTION COMPONENT */
type SectionProps = {
  title: string;
  children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="bg-white rounded-xl p-6 md:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.06)] border-l-4 border-[#D4AF37]">
    <h2 className="text-xl md:text-3xl font-serif text-black mb-4 md:mb-5">
      {title}
    </h2>
    <div className="text-neutral-600 leading-relaxed text-sm md:text-[15px]">
      {children}
    </div>
  </section>
);

export default TermsAndConditions;